import { css, cx } from "@emotion/css";
import type { OctopusTextTheme, OctopusTheme } from "@octopusdeploy/design-system-tokens";
import { lightTheme, textTheme } from "@octopusdeploy/design-system-tokens";
import type { PropsWithChildren } from "react";
import React, { createContext, useMemo } from "react";
import { scrollbarStyles } from "./components/ScrollArea/ScrollArea";
import { getTheme } from "./utils/getTheme";
export interface ThemeProps {
    themeName: ThemeName;
    customFontOverride?: string;
    isFullHeight?: boolean;
}
const DesignSystemThemeContext = createContext<OctopusTheme | undefined>(undefined);
export const DesignSystemThemeProvider = DesignSystemThemeContext.Provider;
export function useDesignSystemTheme() {
    const contextValue = React.useContext(DesignSystemThemeContext);
    if (contextValue === undefined) {
        throw new Error("Context value not provided via a DesignSystemThemeProvider");
    }
    return contextValue;
}
export function useIllustrationThemeName(): IllustrationThemeName {
    const currentTheme = useDesignSystemTheme();
    return currentTheme === lightTheme ? "light" : "dark";
}
export function Theme({ themeName, customFontOverride, isFullHeight, children }: PropsWithChildren<ThemeProps>) {
    const theme: OctopusTheme = getTheme(themeName);
    const updatedTextTheme: OctopusTextTheme = useMemo(() => ({
        ...textTheme,
        fontFamilyDefault: customFontOverride ?? textTheme.fontFamilyDefault,
    }), [customFontOverride]);
    const themeCssVariableProps = useMemo(() => {
        return Object.entries(theme)
            .concat(Object.entries(updatedTextTheme))
            .reduce((prev, [cssVariableName, value]) => {
            return {
                ...prev,
                [`--${cssVariableName}`]: value,
            };
        }, {});
    }, [theme, updatedTextTheme]);
    return (<DesignSystemThemeProvider value={theme}>
            <div style={themeCssVariableProps} className={cx(themeContentStyles, scrollbarStyles, { [fullHeightStyles]: isFullHeight })}>
                {children}
            </div>
        </DesignSystemThemeProvider>);
}
const fullHeightStyles = css({
    height: "100%",
});
const themeContentStyles = css({
    width: "100%",
});
type IllustrationThemeName = "light" | "dark";
export type ThemeName = "light" | "dark";
